var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { convertDtoProductConfToV1 } from "../utilitiesConfiguration.js";
export const isCfgProdConfMessage = (data) => typeof data === "object" && data !== null && "version" in data && "conf" in data;
export const isCfgProdConfMessageV1 = (data) => isCfgProdConfMessage(data) && data.version === "1.0";
export const isCfgProdConfMessageV2 = (data) => isCfgProdConfMessage(data) && data.version === "2.0";
export const getHighestVersionProdConfMessage = (subMessages) => subMessages
    .slice(1)
    .reduce((a, c) => (a.version < c.version ? c : a), subMessages[0]);
export var CfgProdConfMessageVersions;
(function (CfgProdConfMessageVersions) {
    CfgProdConfMessageVersions[CfgProdConfMessageVersions["V1dot0"] = 1] = "V1dot0";
    CfgProdConfMessageVersions[CfgProdConfMessageVersions["V2dot0"] = 2] = "V2dot0";
})(CfgProdConfMessageVersions || (CfgProdConfMessageVersions = {}));
export const STAGE_PROD_CONF_MESSAGE_KEY = "stageprodconf";
/**
 * Base class for connecting the product configuration to an IO channel
 */
export class CfgIOProdConfConnector {
    constructor(_ioManager, _includeInSend // Only for v2.0
    ) {
        this._ioManager = _ioManager;
        this._includeInSend = _includeInSend;
        this._stopListenToMessage = undefined;
        this._stopListenToProdConf = undefined;
        this.setProduct = (product) => __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            const currentProduct = this._product;
            const newProduct = product;
            this._product = newProduct;
            // If same product don't do anything at all
            if (currentProduct !== undefined &&
                newProduct !== undefined &&
                currentProduct.isBackedBySame(newProduct)) {
                return;
            }
            if (currentProduct !== undefined) {
                (_b = this._stopListenToMessage) === null || _b === void 0 ? void 0 : _b.call(this);
                (_c = this._stopListenToProdConf) === null || _c === void 0 ? void 0 : _c.call(this);
            }
            if (newProduct === undefined) {
                return;
            }
            this._send(this.makeSendData(newProduct.getDtoConf(this._includeInSend), true));
            this._stopListenToMessage = CfgIOProdConfConnector.listenForMessage((messages) => __awaiter(this, void 0, void 0, function* () {
                const subMessages = messages.subMessages;
                if (subMessages.length === 0) {
                    console.warn(`${STAGE_PROD_CONF_MESSAGE_KEY} message without any submessages. Unexpected.`);
                    return;
                }
                const highestVersionMessage = getHighestVersionProdConfMessage(subMessages);
                if (isCfgProdConfMessageV1(highestVersionMessage)) {
                    yield newProduct.setApiSelection(highestVersionMessage.conf);
                    return;
                }
                if (isCfgProdConfMessageV2(highestVersionMessage)) {
                    yield newProduct.setDtoConf(highestVersionMessage.conf);
                    return;
                }
                throw new Error("Unknown message version");
            }), this._ioManager);
            this._stopListenToProdConf = CfgIOProdConfConnector.listenForProdConf(newProduct, (conf) => __awaiter(this, void 0, void 0, function* () { return this._send(this.makeSendData(conf, false)); }), this._includeInSend);
        });
        this._send = (data) => this._ioManager.send(STAGE_PROD_CONF_MESSAGE_KEY, data);
        _ioManager.addWarningSupplier(this);
    }
    destroy() {
        var _b, _c;
        (_b = this._stopListenToMessage) === null || _b === void 0 ? void 0 : _b.call(this);
        (_c = this._stopListenToProdConf) === null || _c === void 0 ? void 0 : _c.call(this);
        this._ioManager.removeWarningSupplier(this);
    }
    getWarnings() {
        const product = this._product;
        if (product !== undefined && product.hasRootFeaturesChanged) {
            return [
                "In at least one of the Products, Functional Selection has taken place, thereby changing what root Features are used. The SDK can not yet apply a serialized configuration in these cases. Extracting this data from the SDK for external applications can work, but reinserting the data into Stage will most likely fail.",
            ];
        }
        return [];
    }
    /**
     * Register the callback to listen for Product Configuration messages
     * @returns A function which when called will cancel listening
     */
    static listenForMessage(callback, ioManager) {
        const listener = this.makeMessageListener(callback);
        ioManager.listenForMessage(listener, STAGE_PROD_CONF_MESSAGE_KEY);
        return () => {
            ioManager.stopListenForMessage(listener);
        };
    }
    static listenForProdConf(product, callback, includeInSend) {
        const listener = this.makeProdConfListener(callback, includeInSend);
        product.listenForChange(listener);
        return () => {
            product.stopListenForChange(listener);
        };
    }
}
_a = CfgIOProdConfConnector;
CfgIOProdConfConnector.makeMessage = (conf, initial, sendVersions) => {
    const result = [];
    if ((sendVersions & CfgProdConfMessageVersions.V1dot0) ===
        CfgProdConfMessageVersions.V1dot0) {
        const v1 = {
            version: "1.0",
            conf: convertDtoProductConfToV1(conf, true),
        };
        result.push(v1);
    }
    if ((sendVersions & CfgProdConfMessageVersions.V2dot0) ===
        CfgProdConfMessageVersions.V2dot0) {
        const v2 = {
            version: "2.0",
            conf,
        };
        result.push(v2);
    }
    return { subMessages: result, initial };
};
CfgIOProdConfConnector.makeMessageListener = (callback) => (message) => __awaiter(void 0, void 0, void 0, function* () {
    const prodConfMessage = message;
    yield callback(prodConfMessage);
});
CfgIOProdConfConnector.makeProdConfListener = (callback, includeInSend) => (n) => {
    if (!n.committed) {
        return;
    }
    callback(n.freshRef.getDtoConf(includeInSend));
};
